import { queryOptions, useQueryClient, type QueryClient } from "@tanstack/react-query";
import type { RecordingView } from "@typedoc/contracts/recordings";
import type { TapeEntry } from "@typedoc/interview/events";
import { useEffect, useEffectEvent } from "react";
import {
  chatRecordingsFor,
  isGoing,
  SCREEN_LINK_HOLD_MS,
  type ChatRecordings,
} from "../recordings/chat-recordings";

/**
 * Keyed by scope, as the chats are (`chatsKey`), so a second sign-in or a switch of workspace never
 * reads what was cached for the other.
 */
export const recordingsKey = (scope: string) => ["recordings", scope] as const;
const chatRecordingsKey = (scope: string, chatId: string) =>
  [...recordingsKey(scope), "chat", chatId] as const;
const tapeKey = (scope: string, recordingId: string) =>
  [...recordingsKey(scope), "tape", recordingId] as const;
const screensKey = (scope: string, recordingId: string) =>
  [...recordingsKey(scope), "screens", recordingId] as const;
const packageKey = (scope: string, recordingId: string) =>
  [...recordingsKey(scope), "package", recordingId] as const;

/**
 * A chat's recordings. The island runs in its own window, so the chat learns that a recording
 * paused or finished from the recording's stream (`useFollowRecordings`), not by asking again.
 */
export function chatRecordingsQueryOptions(
  scope: string,
  chatId: string,
  client: () => ChatRecordings = () => chatRecordingsFor(scope),
) {
  return queryOptions({
    queryKey: chatRecordingsKey(scope, chatId),
    queryFn: ({ signal }) => client().list(chatId, signal),
  });
}

/**
 * A recording's whole tape, for the package view. While the recording is going, the chat's
 * stream adds what lands (`useFollowRecordings`).
 */
export function recordingTapeQueryOptions(
  scope: string,
  recording: { id: string },
  client: () => ChatRecordings = () => chatRecordingsFor(scope),
) {
  return queryOptions({
    queryKey: tapeKey(scope, recording.id),
    queryFn: ({ signal }) => client().tape(recording.id, signal),
  });
}

/**
 * A recording's kept screens with links to their images, for the package view (ENG-1024). While
 * the view is open the list is asked for again every `SCREEN_LINK_HOLD_MS`, and every link handed
 * out lasts at least that long and a minute more, so a thumbnail that loads late (they load lazily)
 * never reads an expired link. A page left hidden is not asked for; an image that then fails asks
 * once (`readScreensAgain`).
 */
export function recordingScreensQueryOptions(
  scope: string,
  recording: { id: string },
  client: () => ChatRecordings = () => chatRecordingsFor(scope),
) {
  return queryOptions({
    queryKey: screensKey(scope, recording.id),
    queryFn: ({ signal }) => client().screens(recording.id, signal),
    staleTime: SCREEN_LINK_HOLD_MS,
    refetchInterval: SCREEN_LINK_HOLD_MS,
  });
}

/**
 * A recording's package, for the package timeline (ENG-1028), with links to its screens' images.
 * While it is open it is read again every `SCREEN_LINK_HOLD_MS`, so no link it holds runs out (as
 * `recordingScreensQueryOptions`); a going recording's is also read again as its tape grows
 * (`useFollowRecordings`).
 */
export function recordingPackageQueryOptions(
  scope: string,
  recording: { id: string },
  client: () => ChatRecordings = () => chatRecordingsFor(scope),
) {
  return queryOptions({
    queryKey: packageKey(scope, recording.id),
    queryFn: ({ signal }) => client().package(recording.id, signal),
    staleTime: SCREEN_LINK_HOLD_MS,
    refetchInterval: SCREEN_LINK_HOLD_MS,
  });
}

/**
 * The package view has a screen action whose screen the held list lacks: the list is read again.
 * A read still out may have begun before that screen was kept, so it is begun again rather than
 * left to answer without it (as `addToTape` does for the tape). A list that failed stays failed.
 */
export function readScreensAgain(queryClient: QueryClient, scope: string, recordingId: string) {
  const key = screensKey(scope, recordingId);
  const state = queryClient.getQueryState(key);
  if (state === undefined || state.status === "error") return;
  // A first read has nothing held, so invalidating would only join it: it is canceled first. A
  // later one is canceled by the read that replaces it.
  const first =
    state.fetchStatus === "fetching" && state.data === undefined
      ? queryClient.cancelQueries({ queryKey: key })
      : null;
  void Promise.resolve(first).then(() => queryClient.invalidateQueries({ queryKey: key }));
}

/**
 * The package view's recording has new entries, or ended: its package is read again. A first read
 * still out may have read the tape before they landed, so it is canceled and begun again rather
 * than joined, as `readScreensAgain` does; a later read is replaced by the new one. A package not
 * held (the view is not open) is left alone.
 */
export function readPackageAgain(queryClient: QueryClient, scope: string, recordingId: string) {
  const key = packageKey(scope, recordingId);
  const state = queryClient.getQueryState(key);
  if (state === undefined) return;
  const first =
    state.fetchStatus === "fetching" && state.data === undefined
      ? queryClient.cancelQueries({ queryKey: key })
      : null;
  void Promise.resolve(first).then(() => queryClient.invalidateQueries({ queryKey: key }));
}

/**
 * A recording was deleted: the chat's list lets go of it, and its card and rail row with it. A read
 * of the list still out may have begun before the delete, so it is canceled first: its answer would
 * otherwise put the recording back.
 */
export async function forgetRecording(
  queryClient: QueryClient,
  scope: string,
  chatId: string,
  recordingId: string,
) {
  const key = chatRecordingsKey(scope, chatId);
  await queryClient.cancelQueries({ queryKey: key });
  queryClient.setQueryData(key, (held: RecordingView[] | undefined) =>
    held?.filter((r) => r.id !== recordingId),
  );
}

/**
 * A held tape with entries from the stream added, or null when they do not follow on from it (a
 * gap: the tape is read again). Entries it already has change nothing.
 */
export function withEntries(held: TapeEntry[], entries: TapeEntry[]): TapeEntry[] | null {
  const last = held.at(-1)?.seq ?? 0;
  const fresh = entries.filter((e) => e.seq > last);
  const first = fresh[0];
  if (first === undefined) return held;
  return first.seq === last + 1 ? [...held, ...fresh] : null;
}

/**
 * Adds what the stream sent to the package view's tape, if it holds one. A tape that does not reach
 * the recording's last position after them (a gap) is read again, and so is one whose recording
 * ended, so the package ends on its last entry. A read still out when entries land may have started
 * before they did, so it is begun again rather than left to answer without them.
 */
export function addToTape(
  queryClient: QueryClient,
  key: readonly unknown[],
  recording: RecordingView,
  entries: TapeEntry[],
) {
  const state = queryClient.getQueryState<TapeEntry[]>(key);
  if (state === undefined) return;
  const next = state.data === undefined ? null : withEntries(state.data, entries);
  if (next !== null) queryClient.setQueryData(key, next);
  const behind = next === null || (next.at(-1)?.seq ?? 0) < recording.lastSeq;
  const reading = state.fetchStatus === "fetching";
  if (reading && entries.length > 0) {
    // A first read has nothing held, so it is canceled to be begun again; a later one is canceled
    // by the read that replaces it, which keeps what was just added.
    const first = state.data === undefined ? queryClient.cancelQueries({ queryKey: key }) : null;
    void Promise.resolve(first).then(() => queryClient.invalidateQueries({ queryKey: key }));
  } else if (behind || !isGoing(recording.state)) {
    void queryClient.invalidateQueries({ queryKey: key });
  }
}

/**
 * Follows the chat's going recording, if it has one, on its stream: each change to the recording
 * goes into the chat's list, and what lands into its tape if the package view holds it. The
 * stream closes when the recording stops going, or the chat is left.
 */
export function useFollowRecordings(
  scope: string,
  chatId: string,
  recordings: readonly RecordingView[] | undefined,
  enabled: boolean,
  client: (scope: string) => ChatRecordings = chatRecordingsFor,
) {
  const queryClient = useQueryClient();
  const going = recordings?.find((recording) => isGoing(recording.state));
  const goingId = going?.id;
  // Where it is when following begins; later positions come from the stream itself, so a change
  // of `lastSeq` is not a reason to follow again.
  const follow = useEffectEvent((recordingId: string) =>
    client(scope).watch(recordingId, going?.lastSeq ?? 0, (recording, entries) => {
      queryClient.setQueryData(
        chatRecordingsKey(scope, chatId),
        (held: RecordingView[] | undefined) =>
          held?.map((r) => (r.id === recording.id ? recording : r)),
      );
      addToTape(queryClient, tapeKey(scope, recording.id), recording, entries);
      // The package is folded on the server: an open one is read again with what landed.
      if (entries.length > 0 || !isGoing(recording.state)) {
        readPackageAgain(queryClient, scope, recording.id);
      }
    }),
  );
  useEffect(() => {
    if (!enabled || goingId === undefined) return;
    return follow(goingId);
  }, [enabled, goingId]);
}
